/**
 * The live run: owns the GameState, the engine clock and the frame loop.
 *
 * - The game is created from the session ticket (seed, start and reference
 *   rating) at engine time 0; engine time = performance.now() minus paused
 *   time (clock.ts), exactly what the server's replay assumes.
 * - A requestAnimationFrame loop calls advance() and re-renders with the
 *   frame's engine time, which drives every animation.
 * - Pausing stops the clock and the loop (the pause button, the tab being
 *   hidden, or the window losing focus). Only the player resumes.
 * - Every state change is diffed into audio cues (audioCues.ts), queued on
 *   the engine clock and played when due; stack height sets the tension layer.
 * - At game over it waits for the final animation, then hands the summary to
 *   onGameOver — once. Quitting (endGame) hands over at once.
 *
 * Every answer mode calls the same `submit(value, via)` → core submitAnswer.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  advance,
  createGame,
  endGame,
  paceConfig,
  submitAnswer,
  summarize,
  type GameState,
  type InputVia,
  type RunSummary,
} from '@dropstack/core';
import type { AudioEngine, SessionTicket } from '../contracts.ts';
import { diffAudioCues, INITIAL_CUE_MEMO, takeDueCues, type AudioCue, type CueMemo } from './audioCues.ts';
import { engineTime, isClockPaused, pauseClock, resumeClock, startClock, type PauseClock } from './clock.ts';
import { stackIntensity } from './playfield.ts';
import { TIMING } from './timeline.ts';

export interface GameEngineOptions {
  ticket: SessionTicket;
  audio: AudioEngine;
  onGameOver(summary: RunSummary, info: { quit: boolean }): void;
}

export interface GameEngine {
  state: GameState;
  /** Engine time of this frame. Keeps running after game over, so the final animation can play. */
  now: number;
  paused: boolean;
  /** Answer the falling block. Ignored unless a block is falling (and not while paused). */
  submit(value: string, via: InputVia): void;
  pause(): void;
  resume(): void;
  /** End the run now (from the pause menu). */
  quit(): void;
}

interface Boot {
  state: GameState;
  clock: PauseClock;
  cues: AudioCue[];
  memo: CueMemo;
}

function boot(ticket: SessionTicket): Boot {
  const state = createGame({
    seed: ticket.seed,
    startRating: ticket.startRating,
    referenceRating: ticket.referenceRating,
    now: 0,
    // The ticket's pace, never the live setting: the backend replays at the pace it issued.
    config: paceConfig(ticket.relaxed),
  });
  // The first block spawns at once: its cue (and a danger check) come from diffing against "no game".
  const { cues, memo } = diffAudioCues(null, state, INITIAL_CUE_MEMO);
  return { state, clock: startClock(performance.now()), cues, memo };
}

/** A ref that always holds the latest value (for callbacks that must not re-subscribe). */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

const wallNow = () => performance.now();

export function useGameEngine({ ticket, audio, onGameOver }: GameEngineOptions): GameEngine {
  const [initial] = useState(() => boot(ticket));
  const stateRef = useRef(initial.state);
  const clockRef = useRef(initial.clock);
  const cueQueue = useRef<AudioCue[]>(initial.cues);
  const cueMemo = useRef<CueMemo>(initial.memo);
  const quitRef = useRef(false);
  const handedOver = useRef(false);
  const audioRef = useLatest(audio);
  const onGameOverRef = useLatest(onGameOver);

  const [frame, setFrame] = useState({ state: initial.state, now: 0 });
  const [paused, setPaused] = useState(false);

  const flushCues = useCallback(
    (now: number) => {
      if (!cueQueue.current.length) return;
      const { due, waiting } = takeDueCues(cueQueue.current, now);
      cueQueue.current = waiting;
      for (const cue of due) audioRef.current.play(cue.sfx);
    },
    [audioRef],
  );

  /** Adopt a new state (diffing it for sound) and render it at engine time `now`. */
  const commit = useCallback(
    (next: GameState, now: number) => {
      const prev = stateRef.current;
      if (next !== prev) {
        const { cues, memo } = diffAudioCues(prev, next, cueMemo.current);
        cueMemo.current = memo;
        if (cues.length) cueQueue.current = [...cueQueue.current, ...cues].sort((a, b) => a.at - b.at);
        stateRef.current = next;
      }
      flushCues(now);
      setFrame((f) => (f.state === next && f.now === now ? f : { state: next, now }));
    },
    [flushCues],
  );

  // The frame loop: runs while not paused, until the run has been handed over.
  useEffect(() => {
    if (paused) return;
    let raf = 0;
    const tick = () => {
      if (handedOver.current) return;
      const now = engineTime(clockRef.current, wallNow());
      commit(advance(stateRef.current, now), now);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, commit]);

  const pause = useCallback(() => {
    if (isClockPaused(clockRef.current) || stateRef.current.phase === 'gameOver') return;
    clockRef.current = pauseClock(clockRef.current, wallNow());
    audioRef.current.setPaused(true);
    setPaused(true);
  }, [audioRef]);

  const resume = useCallback(() => {
    if (!isClockPaused(clockRef.current)) return;
    clockRef.current = resumeClock(clockRef.current, wallNow());
    audioRef.current.setPaused(false);
    setPaused(false);
  }, [audioRef]);

  const submit = useCallback(
    (value: string, via: InputVia) => {
      if (isClockPaused(clockRef.current)) return;
      const now = engineTime(clockRef.current, wallNow());
      const prev = stateRef.current;
      const next = submitAnswer(prev, { value, via }, now);
      if (next !== prev) commit(next, now);
    },
    [commit],
  );

  const quit = useCallback(() => {
    const prev = stateRef.current;
    if (prev.phase === 'gameOver') return;
    const now = engineTime(clockRef.current, wallNow());
    const next = endGame(prev, now);
    // endGame logs an `end` event unless the run had already ended on its own by `now`.
    quitRef.current = next.events.at(-1)?.type === 'end';
    if (quitRef.current) cueQueue.current = [];
    commit(next, now);
  }, [commit]);

  // Pause when the tab is hidden or the window loses focus.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') pause();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', pause);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', pause);
    };
  }, [pause]);

  // Music for the length of the run; leave the audio engine clean for the menus.
  useEffect(() => {
    audio.startMusic();
    return () => {
      audio.stopMusic();
      audio.setIntensity(0);
      audio.setPaused(false);
    };
  }, [audio]);

  const { state } = frame;
  const intensity = stackIntensity(state);
  useEffect(() => audioRef.current.setIntensity(intensity), [audioRef, intensity]);

  // Game over: stop the music, let the last animation play, then hand over (once).
  const over = state.phase === 'gameOver';
  useEffect(() => {
    if (!over || handedOver.current) return;
    audioRef.current.stopMusic();
    const handOver = () => {
      if (handedOver.current) return;
      handedOver.current = true;
      onGameOverRef.current(summarize(stateRef.current), { quit: quitRef.current });
    };
    if (quitRef.current) {
      handOver();
      return;
    }
    const timer = setTimeout(handOver, TIMING.GAME_OVER_HOLD_MS);
    return () => clearTimeout(timer);
  }, [over, audioRef, onGameOverRef]);

  return { state, now: frame.now, paused, submit, pause, resume, quit };
}
